/**
 * Auth contracts — login, refresh, /me, change password.
 *
 * The auth-handler Lambda and the public-handler Lambda use these shapes.
 * Tokens are opaque JWTs from Cognito; the frontend stores AccessToken and
 * IdToken and uses AccessToken for subsequent API calls.
 */
import { z } from 'zod';
import { CognitoSub, Email, TenantId, UserId, } from '../primitives/index.js';
import { Role } from '../enums/index.js';
/** Payload emitted by API Gateway Authorizer into downstream handlers. */
export const AuthContextSchema = z.object({
    userId: UserId,
    cognitoSub: CognitoSub,
    email: Email,
    role: Role,
    /** Null for super_admin working outside any tenant context. */
    tenantId: TenantId.nullable(),
    /** Present when the super_admin has switched into a specific tenant via header. */
    impersonatedTenantId: TenantId.nullable().optional(),
});
/** Raw JWT claims returned by `aws-jwt-verify`. */
export const JwtClaimsSchema = z.looseObject({
    sub: z.string().min(1),
    email: z.email().optional(),
    token_use: z.enum(['id', 'access']),
    exp: z.number().int().positive(),
    iat: z.number().int().positive(),
    'cognito:username': z.string().optional(),
});
/** `POST /public/login` request body. */
export const LoginRequestSchema = z.object({
    email: Email,
    password: z.string().min(8).max(128),
});
/** `POST /public/login` success body. */
export const LoginResponseSchema = z.object({
    accessToken: z.string().min(1),
    idToken: z.string().min(1),
    refreshToken: z.string().min(1),
    /** Access/ID token lifetime in seconds. */
    expiresIn: z.number().int().positive(),
    tokenType: z.literal('Bearer'),
});
/** `POST /public/refresh` request body. */
export const RefreshRequestSchema = z.object({
    refreshToken: z.string().min(1),
});
/** `POST /public/refresh` success body — refresh token unchanged, not re-emitted. */
export const RefreshResponseSchema = LoginResponseSchema.omit({ refreshToken: true });
/**
 * `GET /auth/me` success body.
 *
 * The handler wraps the user under a `user` key (legacy shape the frontend
 * consumes). The inner object is loose to allow non-canonical fields (phone,
 * preferences, trial metadata) that the existing UI still uses — Phase 5
 * tightening is planned.
 */
export const MeResponseSchema = z.object({
    user: z.looseObject({
        id: UserId,
        email: Email,
        name: z.string().nullable(),
        role: Role,
        isSuperAdmin: z.boolean(),
        tenant: z
            .looseObject({
            id: TenantId,
            slug: z.string().nullable(),
            name: z.string().nullable(),
            plan: z.string().nullable(),
        })
            .nullable(),
    }),
});
/** `POST /auth/change-password` request body. */
export const ChangePasswordRequestSchema = z.object({
    currentPassword: z.string().min(8).max(128),
    newPassword: z.string().min(8).max(128),
});
/** `POST /auth/change-password` success body. */
export const ChangePasswordResponseSchema = z.object({
    message: z.string().min(1),
});
/** `POST /public/forgot-password` request body. */
export const ForgotPasswordRequestSchema = z.object({
    email: Email,
});
/**
 * `POST /public/forgot-password` success body. Generic message — identical
 * for "account exists" and "account missing" paths to prevent email
 * enumeration.
 */
export const ForgotPasswordResponseSchema = z.object({
    message: z.string().min(1),
});
/** `POST /public/reset-password` request body. */
export const ResetPasswordRequestSchema = z.object({
    email: Email,
    code: z.string().min(1).max(20),
    newPassword: z.string().min(8).max(128),
});
/** `POST /public/reset-password` success body. */
export const ResetPasswordResponseSchema = z.object({
    message: z.string().min(1),
});
/** `POST /auth/logout` success body. */
export const LogoutResponseSchema = z.object({
    message: z.string().min(1),
});
//# sourceMappingURL=index.js.map